import {
  filters,
  API_INFINITY_VALUE,
  FILTER_TYPE,
} from '../../../../../constants';
import type { CustomCompanyFilter } from './types';

import { dots } from '../../../../../functions';

export const generateDividendsFilters = (): CustomCompanyFilter[] => [
  //Dividend Yield
  {
    id: filters.currentDividend,
    label: 'Dividend Yield',
    labels: {
      ...dots(10),
      0: '0%',
      10: '10%',
      12: '∞',
    },
    softMax: 10,
    min: 0,
    max: 12,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["${filters.currentDividend}", "between", 0, 10]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.currentDividend}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    valueResolver: (range) =>
      range.map((value) => (value > 10 ? API_INFINITY_VALUE : value)),
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${
        Number(max) >= API_INFINITY_VALUE ? '∞' : `${max}%`
      }`,
  },
  //Forecast Dividend Yield
  {
    id: filters.dividend_yield_future,
    label: 'Forecast Dividend Yield',
    subLabel: '(Forecasted dividend yield in 3 years)',
    labels: {
      ...dots(10),
      0: '0%',
      10: '10%',
      12: '∞',
    },
    softMax: 10,
    min: 0,
    max: 12,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["${filters.dividend_yield_future}", "between", 0, 10]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.dividend_yield_future}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    valueResolver: (range) =>
      range.map((value) => (value > 10 ? API_INFINITY_VALUE : value)),
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${
        Number(max) >= API_INFINITY_VALUE ? '∞' : `${max}%`
      }`,
  },
  //Buy back Yield
  {
    id: filters.dividend_buyback_yield,
    label: 'Buyback Yield',
    subLabel: '(Percentage of market cap repurchased in the last year)',
    labels: {
      ...dots(10),
      0: '0%',
      10: '10%',
      12: '∞',
    },
    softMax: 10,
    min: 0,
    max: 12,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["${filters.dividend_buyback_yield}", "between", 0, 10]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.dividend_buyback_yield}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    valueResolver: (range) =>
      range.map((value) => (value > 10 ? API_INFINITY_VALUE : value)),
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${
        Number(max) >= API_INFINITY_VALUE ? '∞' : `${max}%`
      }`,
  },
  //Total Shareholder Yield
  {
    id: filters.dividend_total_shareholder_yield,
    label: 'Total Shareholder Yield',
    subLabel: '(Sum of dividend yield and buyback yield)',
    labels: {
      ...dots(10),
      0: '0%',
      10: '10%',
      12: '∞',
    },
    softMax: 10,
    min: 0,
    max: 12,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["${filters.dividend_total_shareholder_yield}", "between", 0, 10]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.dividend_total_shareholder_yield}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    valueResolver: (range) =>
      range.map((value) => (value > 10 ? API_INFINITY_VALUE : value)),
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${
        Number(max) >= API_INFINITY_VALUE ? '∞' : `${max}%`
      }`,
  },
  //Dividend Payment Growth
  {
    id: filters.dividend_payment_growth_annual,
    label: 'Dividend Growth',
    subLabel:
      '(Annual growth rate of dividend payments over the past 3-10 years)',
    labels: {
      ...dots(10),
      0: '0%',
      10: '10%',
      12: '∞',
    },
    softMax: 10,
    min: 0,
    max: 12,
    type: FILTER_TYPE.RANGE,
    defaultValue: `["${filters.dividend_payment_growth_annual}", "between", 0, 10]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.dividend_payment_growth_annual}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    valueResolver: (range) =>
      range.map((value) => (value > 10 ? API_INFINITY_VALUE : value)),
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${
        Number(max) >= API_INFINITY_VALUE ? '∞' : `${max}%`
      }`,
  },
  // Earnings Payout
  {
    id: filters.dividend_payout_ratio,
    label: 'Payout Ratio',
    subLabel: '(Percentage of earnings paid as dividends)',
    labels: {
      ...dots(100, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: 0,
    max: 105,
    defaultValue: `["${filters.dividend_payout_ratio}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.dividend_payout_ratio}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // Cash Flow Payout
  {
    id: filters.dividend_cash_payout_ratio,
    label: 'Cash Payout Ratio',
    subLabel: '(Percentage of free cash flow paid as dividends)',
    labels: {
      ...dots(100, { step: 5 }),
      0: '0%',
      25: '25%',
      50: '50%',
      100: '100%',
      105: '∞',
    },
    min: 0,
    max: 105,
    defaultValue: `["${filters.dividend_cash_payout_ratio}", "between", 25, 50]`,
    rule: (range) => {
      if (range instanceof Array) {
        const [min, max] = range;
        return `["${filters.dividend_cash_payout_ratio}", "between", ${min}, ${max}]`;
      }
      return '';
    },
    type: FILTER_TYPE.RANGE,
    valueResolver: (range) => {
      const [min, max] = range;
      return [min, max > 100 ? API_INFINITY_VALUE : max];
    },
    titleFormatter: ([_, __, min, max]) =>
      `is between ${min}% and ${max === API_INFINITY_VALUE ? '∞' : max}%`,
  },
  // Upcoming payment
  {
    id: filters.upcomingDividend,
    label: 'Only show companies with an upcoming dividend payment',
    rule: `["${filters.upcomingDividend}", "=", 1]`,
    defaultValue: `["${filters.upcomingDividend}", "=", 1]`,
  },
];
